import retailImage from "./assets/case-retail-discovery.webp";
import hotelImage from "./assets/case-hotel-community.webp";
import restaurantImage from "./assets/case-restaurant-community.webp";

// Three concept case studies, one per segment. Every segment uses the same three plans.
// None describes a real customer or a measured result.
export const plans = [
  { name: "Starter", price: 99, uploads: "5 uploads a day", viewers: "100 viewers a day" },
  { name: "Growth", price: 349, uploads: "20 uploads a day", viewers: "1,000 viewers a day" },
  { name: "Scale", price: 999, uploads: "50 uploads a day", viewers: "Unlimited viewers" },
];

export const posts = [
  {
    slug: "retail",
    segment: "Retail",
    date: "October 2026",
    readTime: "3 min read",
    title: "A product story with somewhere useful to go",
    dek: "How a multi-location apparel retailer could turn customer-made reels into repeat visits inside the app it already owns.",
    image: retailImage,
    imageAlt: "Illustration of apparel discovery through customer-created content",
    imageCaption: "Illustration, not a customer",
    business: "A retailer with several stores, a shopping app and a loyalty program. It pays for ads to find new shoppers, but once a purchase is made the relationship has nowhere to continue between visits.",
    steps: [
      ["Share", "A customer or creator posts a look in the retailer's community."],
      ["Review", "The retailer's team approves each post before it goes live."],
      ["Discover", "A product tag opens the item in the retailer's own app, cart and checkout."],
    ],
    fit: {
      plan: "Growth",
      why: "20 uploads and 1,000 viewers a day suit a retailer with a few locations and an active app.",
      unit: "extra repeat orders a month",
      benchmark: "about $38 per paid order on Meta (Triple Whale, 2025 e-commerce median)",
      payback: { Starter: "3", Growth: "9", Scale: "26" },
    },
    caveat: "A live catalog, cart or checkout connection has not yet been demonstrated. This is a workflow concept, not a result.",
  },
  {
    slug: "hospitality",
    segment: "Hospitality",
    date: "October 2026",
    readTime: "3 min read",
    title: "Hotel experiences, powered by guests",
    dek: "How a hotel could let guests share the moments that make a stay worth booking, on the property's own channels.",
    image: hotelImage,
    imageAlt: "Illustration of hotel guests sharing a moment from their stay",
    imageCaption: "Illustration, not a customer",
    business: "A hotel or small group whose guests already photograph their stay. The best of it ends up on social feeds the hotel does not control, while the hotel pays booking sites and ads to reach the next guest.",
    steps: [
      ["Share", "Guests and staff post short videos of the property, dining and local activities."],
      ["Review", "The hotel approves each post before it is visible. Nothing publishes by default."],
      ["Book", "A relevant video links to the hotel's own booking or experience page."],
    ],
    fit: {
      plan: "Growth",
      why: "A single property with steady guest traffic fits 20 uploads and 1,000 viewers a day.",
      unit: "room-nights of revenue a month",
      benchmark: "about $162 average daily rate (AHLA, 2025, via OysterLink)",
      payback: { Starter: "1", Growth: "2", Scale: "6" },
    },
    caveat: "No hotel loyalty or booking connection is live. Identity, data and privacy design would need joint review before any pilot.",
  },
  {
    slug: "restaurants",
    segment: "Restaurants",
    date: "October 2026",
    readTime: "3 min read",
    title: "Turn a great meal into a local story",
    dek: "How a restaurant could invite diners to share a dish and give them a reason to come back, without paying for the reach again.",
    image: restaurantImage,
    imageAlt: "Illustration of diners capturing a restaurant experience",
    imageCaption: "Illustration, not a customer",
    business: "A restaurant spending a few thousand dollars a month on ads and delivery apps to win new diners. After the first meal, most of them are never reached again.",
    steps: [
      ["Capture", "A diner shares a dish, a team moment or a neighborhood favorite."],
      ["Review", "A manager checks each submission before it can appear."],
      ["Reward", "Eligible uploads and views earn points that bring people back."],
    ],
    fit: {
      plan: "Growth",
      why: "A busy single location fits 20 uploads and 1,000 viewers a day. A quiet one can start on Starter.",
      unit: "extra repeat orders a month",
      benchmark: "about $25 per paid order (WordStream 2025; Webtonic 2026)",
      payback: { Starter: "4", Growth: "14", Scale: "40" },
    },
    caveat: "Reward rules and moderation would need to be configured and tested. This is a workflow concept, not a result.",
  },
];
